import { all, put, takeLatest, call, select, delay } from 'redux-saga/effects';
import { LodgeEnquiryActionTypes, LodgeEnquiryPayloadType } from 'types';
import {
  fetchLastEnquirySuccess,
  fetchEnquiryJobSuccess,
  fetchLodgeEnquiryFailure,
  fetchLodgeEnquirySuccess,
  cloneExistingEnquiryFailure,
  saveEnquiryPresetsSuccess,
  saveEnquiryPresetsFailure,
  deleteEnquiryPresetsSuccess,
  deleteEnquiryPresetsFailure
} from './actions';
import Services from 'services';
import { fetchAppFailure } from 'redux/app/actions';
import {
  fetchDesignActivity,
  fetchEnquirySuccess,
  fetchExcavationActivity
} from 'redux/enquiries';
import { fetchUserSuccess } from 'redux/user/actions';
import Utils from 'utilities/helper.util';

function* doNewLodgeEnquiryInit() {
  try {
    const designActResponse = yield call(
      Services.enquiry.fetchDesignActivityService
    );
    if (!designActResponse.errors) {
      yield put(fetchDesignActivity(designActResponse));
    } else {
      yield put(fetchAppFailure(designActResponse.errors[0].message));
    }
    // EXCAVATION ACTIVITY API CALL
    const excavationActResponse = yield call(
      Services.enquiry.fetchExcavationActivityService
    );
    if (!excavationActResponse.errors) {
      yield put(fetchExcavationActivity(excavationActResponse));
    } else {
      yield put(fetchAppFailure(excavationActResponse.errors[0].message));
    }
  } catch (error) {
    yield put(fetchAppFailure(error));
  }
  // DESIGN ACTIVITY API CALL
}

function* doNewLodgeEnquiry(request: any) {
  const getSession = (state: any) => state.session;
  const session: any = yield select(getSession);
  if (session.isAuthenticated && request) {
    try {
      const { userPayload, enquiryPayload }: any = request.payload;
      const {
        autoRenewToggle,
        autoRenewUntil,
        digEndAt,
        digStartAt,
        Address
      } = enquiryPayload;
      const { line1, line2, locality, postcode, state, country } = Address;
      const enquiryAddress = {
        line1,
        line2,
        locality,
        postcode,
        state,
        country
      };

      const enquiryPayloadPack: any = {
        ...enquiryPayload,
        Address: enquiryAddress,
        digStartAt: Utils.toDateAPIFormat(digStartAt),
        digEndAt: Utils.toDateAPIFormat(digEndAt),
        autoRenewUntil:
          autoRenewToggle && autoRenewUntil
            ? Utils.toDateAPIFormat(autoRenewUntil)
            : undefined,
        //Get current user device timezone to ensure we will compare date with correct timezone at API
        userTimezone: Utils.getTimeZone()
      };

      const payload: LodgeEnquiryPayloadType = {
        ...enquiryPayloadPack,
        source: 'WEB'
      };

      if (userPayload) {
        const { Address, phoneNumber, companyName } = userPayload;
        yield call(Services.user.patchUserDetails, {
          Address,
          phoneNumber,
          companyName: companyName.trim() === '' ? undefined : companyName
        });
      }
      const response = yield call(
        Services.enquiry.lodgeEnquiryRequest,
        payload
      );
      if (!response.error) {
        yield put(fetchLodgeEnquirySuccess(response));
        //  yield put(fetchAppSuccess());
      } else {
        yield put(fetchLodgeEnquiryFailure(response.error));
        yield put(fetchAppFailure(response.error));
      }
    } catch (error) {
      // Extract the actual error message from the response
      const err: any = error;
      const errorMessage =
        (err &&
          err.response &&
          err.response.data &&
          err.response.data.error &&
          err.response.data.error.message) ||
        (err && err.message) ||
        'Internal Server Error.';
      yield put(fetchLodgeEnquiryFailure(errorMessage));
      yield put(fetchAppFailure(errorMessage));
    }
  }
}

function* doEnquiryJobRequest(request: any) {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      yield delay(5000);
      const response = yield call(
        Services.enquiry.fetchJobDetailsById,
        request.payload
      );
      if (!response.errors) {
        yield put(fetchEnquiryJobSuccess(response));
        // yield put(fetchAppSuccess());
      } else {
        yield put(fetchAppFailure(response.error));
      }
    } catch (error) {
      yield put(fetchAppFailure(error.message));
    }
  }
}

function* doLastEnquiryRequest() {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      const params = {
        limit: 1,
        orderBy: '-created_at',
        include: 'Address',
        filter: `userId:${session.currentUser.id}`
      };
      const response = yield call(
        Services.enquiry.fetchAllEnquiriesService,
        params
      );
      if (!response.errors && response?.Enquiries.length > 0) {
        yield put(fetchLastEnquirySuccess(response.Enquiries[0]));
      }
    } catch (error) {
      // Do nothing
    }
  }
}

function* doCloneExistingEnquiryRequest(request: any) {
  try {
    const response = yield call(
      Services.enquiry.fetchEnquiryByIdService,
      request.payload
    );
    if (!response.errors && !response.error) {
      yield put(fetchEnquirySuccess(response));
    }
  } catch (error) {
    yield put(cloneExistingEnquiryFailure(error));
  }
}

function* doSaveEnquiryPresets(request: any) {
  try {
    const currentUser = yield call(Services.user.fetchUser);
    const currPreferences = currentUser?.preferences;
    const preferences = Utils.isObjectEmpty(currPreferences)
      ? { enquiryPresets: request.payload }
      : { ...currPreferences, enquiryPresets: request.payload };
    const response = yield call(Services.user.patchUserDetails, {
      preferences
    });
    if (response.error) {
      return yield put(saveEnquiryPresetsFailure(response.error.message));
    }
    const userResponse = yield call(Services.user.fetchUser);
    yield put(saveEnquiryPresetsSuccess());
    yield put(fetchUserSuccess(userResponse));
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(saveEnquiryPresetsFailure(errMsg));
  }
}

function* doDeleteEnquiryPresets() {
  try {
    const currentUser = yield call(Services.user.fetchUser);
    const currPreferences = currentUser?.preferences;
    const preferences = { ...currPreferences, enquiryPresets: null };
    const response = yield call(Services.user.patchUserDetails, {
      preferences
    });
    if (response.error) {
      return yield put(deleteEnquiryPresetsFailure(response.error.message));
    }
    const userResponse = yield call(Services.user.fetchUser);
    yield put(deleteEnquiryPresetsSuccess());
    yield put(fetchUserSuccess(userResponse));
  } catch (err) {
    const errMsg = err?.response?.data?.error?.message || err?.toString();
    yield put(deleteEnquiryPresetsFailure(errMsg));
  }
}

function* onNewLodgeEnquiryInit() {
  yield takeLatest(
    LodgeEnquiryActionTypes.FETCH_LODGE_ENQUIRY_INIT,
    doNewLodgeEnquiryInit
  );
}

function* onNewLodgeEnquiry() {
  yield takeLatest(
    LodgeEnquiryActionTypes.FETCH_LODGE_ENQUIRY_REQUEST,
    doNewLodgeEnquiry
  );
}

function* onEnquiryJobRequest() {
  yield takeLatest(
    LodgeEnquiryActionTypes.FETCH_ENQUIRY_JOB_REQUEST,
    doEnquiryJobRequest
  );
}

function* onLastEnquiryRequest() {
  yield takeLatest(
    LodgeEnquiryActionTypes.FETCH_LAST_ENQUIRY_REQUEST,
    doLastEnquiryRequest
  );
}

function* onCloneExistingEnquiryRequest() {
  yield takeLatest(
    LodgeEnquiryActionTypes.CLONE_EXISTING_ENQUIRY_REQUEST,
    doCloneExistingEnquiryRequest
  );
}

function* onSaveEnquiryPresets() {
  yield takeLatest(
    LodgeEnquiryActionTypes.SAVE_ENQUIRY_PRESETS_START,
    doSaveEnquiryPresets
  );
}

function* onDeleteEnquiryPresets() {
  yield takeLatest(
    LodgeEnquiryActionTypes.DELETE_ENQUIRY_PRESETS_START,
    doDeleteEnquiryPresets
  );
}

function* lodgeEnquirySagas() {
  yield all([
    call(onNewLodgeEnquiryInit),
    call(onNewLodgeEnquiry),
    call(onEnquiryJobRequest),
    call(onLastEnquiryRequest),
    call(onCloneExistingEnquiryRequest),
    call(onSaveEnquiryPresets),
    call(onDeleteEnquiryPresets)
  ]);
}
export default lodgeEnquirySagas;
